/**
 * Block-choice score: a behavioural measure of choice relevance over the latest scored decisions.
 *
 *   score = 100 × (correct + 0.5 × related mistakes) / scored choices in the window
 *
 * The window holds the latest 10 scored choices; the oldest drops out when an 11th arrives. Before 10
 * choices exist the score uses those recorded so far and reports how many are included. With none it is
 * "insufficient data" (`score: null`). The 1 / 0.5 / 0 weights and the 10-choice window are proposed
 * starting settings, subject to pilot results, so both are options.
 *
 * This measures choice relevance while its relationship to attention is being tested. It is kept apart
 * from any EEG indicator; one incorrect choice does not establish that the player was inattentive.
 */
import type { ChoiceOutcome } from "./types.ts";

export interface ChoiceScoreOptions {
  /** Scored choices in the window (default 10). */
  window?: number;
  /** Credit per outcome (default correct 1, related 0.5, irrelevant 0). */
  weights?: Record<ChoiceOutcome, number>;
}

export interface ChoiceScoreSnapshot {
  /** 0–100, or null for "insufficient data" (no scored choices yet). */
  score: number | null;
  /** Scored choices included in the score (at most the window size). */
  included: number;
  /** The window is full. */
  full: boolean;
  correct: number;
  related: number;
  irrelevant: number;
  /** Scored choices seen in total, including those that have left the window. */
  total: number;
}

export const DEFAULT_CHOICE_WINDOW = 10;
export const DEFAULT_CHOICE_WEIGHTS: Readonly<Record<ChoiceOutcome, number>> = Object.freeze({ correct: 1, related: 0.5, irrelevant: 0 });

export class BlockChoiceScore {
  readonly window: number;
  readonly weights: Readonly<Record<ChoiceOutcome, number>>;
  private recent: ChoiceOutcome[] = [];
  private total = 0;

  constructor(opts: ChoiceScoreOptions = {}) {
    this.window = opts.window ?? DEFAULT_CHOICE_WINDOW;
    if (!Number.isInteger(this.window) || this.window < 1) throw new RangeError("window must be a positive whole number");
    this.weights = Object.freeze({ ...DEFAULT_CHOICE_WEIGHTS, ...opts.weights });
  }

  /** Adds one scored choice and returns the recalculated score. */
  add(outcome: ChoiceOutcome): ChoiceScoreSnapshot {
    if (!(outcome in this.weights)) throw new RangeError(`unknown choice outcome "${String(outcome)}"`);
    this.recent.push(outcome);
    if (this.recent.length > this.window) this.recent.shift();
    this.total++;
    return this.snapshot();
  }

  reset(): void {
    this.recent = [];
    this.total = 0;
  }

  snapshot(): ChoiceScoreSnapshot {
    const count = (o: ChoiceOutcome) => this.recent.filter((r) => r === o).length;
    const correct = count("correct"), related = count("related"), irrelevant = count("irrelevant");
    const included = this.recent.length;
    const credit = correct * this.weights.correct + related * this.weights.related + irrelevant * this.weights.irrelevant;
    return {
      score: included ? Math.round((1000 * credit) / included) / 10 : null,
      included,
      full: included === this.window,
      correct,
      related,
      irrelevant,
      total: this.total,
    };
  }
}

/** "75 (10 choices)", "50 (2 of 10 choices)" or "insufficient data". */
export function formatChoiceScore(s: ChoiceScoreSnapshot, window = DEFAULT_CHOICE_WINDOW): string {
  if (s.score === null) return "insufficient data";
  const n = s.full ? `${s.included} choices` : `${s.included} of ${window} choice${window === 1 ? "" : "s"}`;
  return `${s.score} (${n})`;
}
